import { useEffect, useState } from "react";
import type { AgentDTO, SkillDetailDTO } from "../../shared/types";
import { useUser } from "../App";
import { get, patch, post } from "../lib/api";
import { scopePath } from "../lib/scopes";
import { List, ListRow } from "./List";
import { Tabs } from "./Tabs";
import { Dialog } from "./Dialog";
import { MultiSelect } from "./MultiSelect";
import { TextLink } from "./TextLink";
import { SkillFiles } from "./SkillFiles";
import { AgentChoice, names } from "./SkillForm";
import { agentsForPersonSkills, InactiveNote, inactiveAgentIds, LevelTag, levelLabel, SkillWho } from "./SkillWho";
import { AgentLineWarning, Badge, Button, ErrorText, Field, FieldGroup, timeAgo } from "./ui";

type Tab = "files" | "details";

/** Every edition, newest first. Restoring an old one appends a copy of it, so the history keeps both. */
function Versions({ skill, onChanged }: { skill: SkillDetailDTO; onChanged: () => void }) {
  const [error, setError] = useState<string | null>(null);
  return (
    <List title="Versions" hint="Saving other files or another description appends a version; every load records the one it was served." footer={error && <ErrorText error={error} />}>
      {skill.versions.map((v) => (
        <ListRow
          key={v.id}
          title={<span className="font-mono text-xs">v{v.version}</span>}
          subtitle={<span className="text-sm">{v.createdBy} · {timeAgo(v.createdAt)}</span>}
          trailing={
            v.version === skill.version ? (
              <Badge tone="emerald">Current</Badge>
            ) : (
              <Button onClick={() => post(`/api/skills/${skill.id}/versions/${v.version}/restore`).then(onChanged, (e) => setError(e.message))}>
                Restore
              </Button>
            )
          }
        />
      ))}
    </List>
  );
}

/** Who has it, what it points at, who loaded it, and whether it is offered at all. */
function Details({ skill, agents, onChanged }: { skill: SkillDetailDTO; agents: AgentDTO[]; onChanged: () => void }) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const me = useUser();
  // A person's skill is offered only the agents it may be on — plus any it is on and no longer reaches, so one
  // left behind by an ownership that has gone can still be taken off.
  const personOptions = [
    ...agentsForPersonSkills(agents, skill.userId).map((a) => ({ value: a.id, label: a.name, hint: a.slug })),
    ...inactiveAgentIds(skill, agents).map((id) => ({ value: id, label: agents.find((a) => a.id === id)?.name ?? "an agent", hint: "inactive" })),
  ];
  // An agents' own skill is the owners' of every agent that has it: while one of those is not the reader's to
  // answer for, the platform refuses any change, so none is offered.
  const notYours = skill.level === "agent" ? agents.filter((a) => skill.grantedAgentIds.includes(a.id) && !a.youAnswerFor) : [];
  const whose = skill.userId === me.id ? "your" : `${skill.userName?.split(" ")[0] ?? "its person"}'s`;
  const save = async (body: Record<string, unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await patch(`/api/skills/${skill.id}`, body);
      onChanged();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="text-sm text-muted">
        {skill.description}
        <AgentLineWarning text={skill.description} what="skill" />
        <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
          <span>Created {timeAgo(skill.createdAt)} by {skill.createdBy}</span>
          <span>Last saved {timeAgo(skill.updatedAt)}</span>
          <LevelTag level={skill.level} />
          <span>{levelLabel(skill)}</span>
          {skill.archivedAt && <Badge tone="amber">Archived {timeAgo(skill.archivedAt)}</Badge>}
        </div>
      </div>

      {skill.level === "scope" ? (
        <FieldGroup label="Used by" hint="A scope's skill is given to no agent: every agent working under that scope has it.">
          <div className="text-sm">
            {skill.subjectId && skill.scopeKey ? (
              <TextLink to={scopePath({ kind: skill.scopeKey.split(":")[0]!, id: skill.subjectId })}>
                <SkillWho skill={skill} agents={agents} />
              </TextLink>
            ) : (
              <SkillWho skill={skill} agents={agents} />
            )}
          </div>
        </FieldGroup>
      ) : (
        <FieldGroup
          label="Used by"
          hint={
            skill.level === "person"
              ? `${skill.userName ?? "One person"}'s own skill: the agents it is shared with follow it when they work for them, hand-offs included. It goes on the agents that take people's skills and on the agents ${skill.userName ?? "its person"} owns, and nowhere else.`
              : "A skill for everyone an agent works with reaches the agents it is attached to, whoever they work for."
          }
        >
          <div className={`space-y-2 ${busy ? "pointer-events-none opacity-60" : ""}`}>
            {skill.level === "agent" ? (
              <AgentChoice
                agents={agents}
                every={skill.allAgents}
                chosen={skill.grantedAgentIds}
                onEvery={(every) => void save(every ? { allAgents: true, agentIds: [] } : { allAgents: false })}
                onChosen={(agentIds) => void save({ agentIds })}
                locked={notYours.length ? `Only the owners of ${names(notYours)} can change which agents have it.` : undefined}
              />
            ) : (
              // A person's skill is never given to every agent: that would reach every agent made later too.
              <MultiSelect
                options={personOptions}
                selected={skill.grantedAgentIds}
                onChange={(agentIds) => void save({ agentIds })}
                placeholder="Choose agents"
                all={whose === "your" ? "All your agents" : `All of ${whose} agents`}
                summary={(chosen) => (chosen.length ? chosen.map((o) => o.label).join(", ") : "No agent has this skill yet")}
              />
            )}
            <InactiveNote skill={skill} agents={agents} />
          </div>
        </FieldGroup>
      )}

      <List title="Connections" empty={skill.references.length === 0 && skill.referencedBy.length === 0 ? "Nothing points at this skill, and it points at nothing. Write [[another-skill]] in the instructions to link one." : undefined}>
        {skill.references.map((ref) => (
          <ListRow
            key={`ref-${ref.name}`}
            title={
              <>
                <span className="text-xs text-muted">points at</span>
                {ref.skillId ? (
                  <TextLink to={`/admin/skills/${ref.skillId}`} className="font-mono text-xs">{ref.name}</TextLink>
                ) : (
                  <>
                    <span className="font-mono text-xs">{ref.name}</span>
                    <Badge tone="amber">no such skill on its shelves</Badge>
                  </>
                )}
              </>
            }
          />
        ))}
        {skill.referencedBy.map((source) => (
          <ListRow
            key={`by-${source.id}`}
            title={
              <>
                <span className="text-xs text-muted">pointed at by</span>
                <TextLink to={`/admin/skills/${source.id}`} className="font-mono text-xs">{source.name}</TextLink>
              </>
            }
          />
        ))}
      </List>

      <Versions skill={skill} onChanged={onChanged} />

      <List title={`Loads · ${skill.reads}`} hint="Loads by agents' runs, through pandas skill read. Opening a skill here is not counted." empty="No run has loaded it yet.">
        {skill.recentReads.map((read, i) => (
          <ListRow
            key={`${read.runId}-${i}`}
            title={
              <>
                <span className="w-20 shrink-0 text-xs text-muted">{timeAgo(read.readAt)}</span>
                <span className="font-mono text-xs">{read.agentSlug ?? "—"}</span>
                {read.scopeKey && <span className="text-xs text-muted">{read.scopeKey}</span>}
                <span className="text-xs text-muted">v{read.version}</span>
              </>
            }
            trailing={read.runId && <TextLink to={`/admin/runs/${read.runId}`}>the run</TextLink>}
          />
        ))}
      </List>

      <ErrorText error={error} />
      <div className="flex justify-end border-t border-line pt-4">
        <Button variant={skill.archivedAt ? "default" : "danger"} disabled={busy} onClick={() => void save({ archived: !skill.archivedAt })}>
          {skill.archivedAt ? "Un-archive" : "Archive"}
        </Button>
      </div>
    </div>
  );
}

/** One skill, as a person edits it: its files, and everything about where it applies and who has loaded it. */
export function SkillDetail({ skillId, agents, dropped, onClose, onChanged }: { skillId: string; agents: AgentDTO[]; dropped?: string[]; onClose: () => void; onChanged: () => void }) {
  const [skill, setSkill] = useState<SkillDetailDTO | null>(null);
  const [tab, setTab] = useState<Tab>("files");
  const [error, setError] = useState<string | null>(null);

  const load = () => get<SkillDetailDTO>(`/api/skills/${skillId}`).then(setSkill, (e) => setError(e.message));
  useEffect(() => {
    void load();
  }, [skillId]);

  const saved = (next: SkillDetailDTO) => {
    setSkill(next);
    onChanged();
  };
  return (
    <Dialog open onClose={onClose} size="xl" title={skill ? `${skill.name} · v${skill.version}` : "Skill"}>
      {!skill ? (
        <ErrorText error={error} />
      ) : (
        <div className="space-y-4">
          <Tabs
            label="Section"
            value={tab}
            onChange={setTab}
            tabs={[
              { key: "files", label: "Files", count: skill.fileCount },
              { key: "details", label: "Details" },
            ]}
          />
          <ErrorText error={error} />
          {/* Both stay mounted: switching tabs must never throw away an edit the person has not saved. */}
          <div className={tab === "files" ? undefined : "hidden"}>
            <SkillFiles skill={skill} dropped={dropped} onSaved={saved} />
          </div>
          <div className={tab === "details" ? undefined : "hidden"}>
            <Details skill={skill} agents={agents} onChanged={() => void load().then(onChanged)} />
          </div>
        </div>
      )}
    </Dialog>
  );
}
